// Runs just before a lab report prints (react-to-print's `print` callback,
// inside the print iframe after the content has been copied and loaded).
//
// 1. A report that is only a bit taller than one A4 page is shrunk (CSS zoom)
//    so the whole report - including the doctor info / footer group - stays on
//    page 1. Without this the footer group (.report-page-bottom, which has
//    break-inside: avoid) jumps to page 2 and leaves an empty gap on page 1.
// 2. A report too long to shrink that far really needs several pages. Then the
//    template's header (elements marked .report-repeat-header) is moved into a
//    <thead>, which browsers repeat at the top of every printed page, and the
//    footer group (.report-page-bottom: doctor info, health commission logo /
//    Reg.No) into a <tfoot>, which browsers repeat at the bottom of every page.

const MM_TO_PX = 96 / 25.4;
const PAGE_HEIGHT_MM = 297; // A4
const PAGE_MARGIN_MM = 3;   // must match @page { margin } in UI/Template.css
const PAGE_WIDTH_MM = 210;
// The print dialog can override @page margins (Custom / Default margins) after
// this code has run, so decide "fits on one page" against a page with up to
// this much top/bottom margin. Then a custom margin doesn't push the footer
// group onto an extra, almost empty page.
const PAGE_MARGIN_BUDGET_MM = 10;

// Screen and print layout differ slightly (font hinting, rounding), so keep a
// little spare room: a fixed number of px plus a percentage of the page.
const SAFETY_PX = 6;
const SAFETY_RATIO = 0.015;

// Smallest shrink still applied to keep a report on one page. Reports that
// would need more than this print on several pages with a repeated header.
const MIN_SCALE = 0.8;

// Copies every rule inside an @media print block into a plain stylesheet so the
// iframe (which renders with screen media) can be measured with print styles.
const collectPrintRules = (rules, insidePrint, out) => {
  Array.from(rules || []).forEach((rule) => {
    if (typeof CSSMediaRule !== 'undefined' && rule instanceof CSSMediaRule) {
      const isPrint = insidePrint || /\bprint\b/i.test(rule.media.mediaText);
      collectPrintRules(rule.cssRules, isPrint, out);
    } else if (insidePrint && !(typeof CSSPageRule !== 'undefined' && rule instanceof CSSPageRule)) {
      out.push(rule.cssText);
    }
  });
};

const px = (value) => parseFloat(value) || 0;

// Space taken at the bottom of every page by position:fixed footers
// (Templates 5-7). Print repeats fixed elements on each page, on top of the
// content, so a multi-page report has to keep clear of them on every page.
const fixedFooterReserve = (win, page) => {
  let reserve = 0;
  page.querySelectorAll('*').forEach((el) => {
    const cs = win.getComputedStyle(el);
    if (cs.position === 'fixed' && cs.bottom !== 'auto' && cs.display !== 'none') {
      reserve = Math.max(reserve, el.getBoundingClientRect().height + px(cs.bottom));
    }
  });
  return reserve;
};

// Wraps the header and everything after it in a table so the header repeats:
//   <table><thead>header</thead><tbody>tests</tbody><tfoot>footer group</tfoot></table>
const repeatHeaderOnEveryPage = (doc, win, page) => {
  const marked = Array.from(page.querySelectorAll('.report-repeat-header'));
  if (!marked.length) return false;

  const parent = marked[0].parentNode;
  const first = marked[0];
  const last = marked.filter((el) => el.parentNode === parent).pop();
  const reserve = fixedFooterReserve(win, page);

  const table = doc.createElement('table');
  table.className = 'report-repeat-table';
  table.style.cssText = 'width:100%;border-collapse:collapse;border-spacing:0;';
  const cell = (section) => {
    const td = doc.createElement('td');
    td.style.cssText = 'padding:0;border:0;';
    const tr = doc.createElement('tr');
    tr.appendChild(td);
    section.appendChild(tr);
    table.appendChild(section);
    return td;
  };
  const headCell = cell(doc.createElement('thead'));
  const bodyCell = cell(doc.createElement('tbody'));
  const bottom = page.querySelector('.report-page-bottom');
  const footCell = bottom || reserve > 0 ? cell(doc.createElement('tfoot')) : null;

  parent.insertBefore(table, first);
  let node = first;
  let target = headCell;
  while (node) {
    const next = node.nextSibling;
    target.appendChild(node);
    if (node === last) target = bodyCell;
    node = next;
  }

  // tfoot repeats at the bottom of every page, so the doctor info, health
  // commission logo / Reg.No and copyright print on each page, not only after
  // the last test.
  if (bottom) {
    footCell.appendChild(bottom);
    bottom.style.marginTop = '0';
  }
  if (reserve > 0) {
    // Keeps the rows and the footer group clear of the position:fixed footer
    // bar (Templates 5-7). Replaces the body padding the templates use for the
    // same purpose, which only works on the last page.
    const spacer = doc.createElement('div');
    spacer.style.height = `${Math.ceil(reserve)}px`;
    footCell.appendChild(spacer);
    doc.body.style.paddingBottom = '0';
    if (bottom) bottom.style.paddingBottom = '0'; // Template 7's own clearance

  }

  // The "fill one page" flex layout is meaningless across several pages.
  page.style.minHeight = '0';
  page.style.display = 'block';
  page.querySelectorAll('.report-page-fill').forEach((el) => { el.style.display = 'block'; });
  return true;
};

export const fitReportToPage = (iframe) => {
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;

  try {
    // Lay the copy out at exactly the printable page size so line wrapping and
    // % positions match paper: no iframe border, no scrollbar.
    iframe.removeAttribute('width');
    iframe.removeAttribute('height');
    iframe.style.width = `${PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM}mm`;
    // Height uses the conservative margin so 100vh (the print min-height in
    // ReportLayout.css) never resolves taller than the printable area.
    iframe.style.height = `${PAGE_HEIGHT_MM - 2 * PAGE_MARGIN_BUDGET_MM}mm`;
    iframe.style.border = '0';

    const printRules = [];
    Array.from(doc.styleSheets).forEach((sheet) => {
      try {
        collectPrintRules(sheet.cssRules, false, printRules);
      } catch (e) {
        // cross-origin stylesheet - its rules cannot be read, skip it
      }
    });
    const simulated = doc.createElement('style');
    simulated.textContent = `${printRules.join('\n')}\nhtml { overflow: hidden !important; }`;
    doc.head.appendChild(simulated);

    const bodyStyle = win.getComputedStyle(doc.body);
    const pageHeightPx =
      (PAGE_HEIGHT_MM - 2 * PAGE_MARGIN_BUDGET_MM) * MM_TO_PX -
      px(bodyStyle.marginTop) - px(bodyStyle.marginBottom) -
      px(bodyStyle.paddingTop) - px(bodyStyle.paddingBottom);

    doc.querySelectorAll('.report-page').forEach((page) => {
      const style = win.getComputedStyle(page);
      const budget =
        pageHeightPx * (1 - SAFETY_RATIO) - SAFETY_PX -
        px(style.marginTop) - px(style.marginBottom);

      // Natural content height, without the "fill one page" min-height.
      const savedMinHeight = page.style.minHeight;
      page.style.minHeight = '0';
      const natural = Math.max(page.getBoundingClientRect().height, page.scrollHeight);
      page.style.minHeight = savedMinHeight;

      const scale = natural > budget ? budget / natural : 1;
      let result = 'fits on one page';
      if (scale < 1 && scale >= MIN_SCALE) {
        // Zoom also shrinks the "fill the page" min-height, which would lift
        // the footer group off the page bottom. Scale it back up, keeping it
        // in vh so it still follows the printable height chosen in the dialog.
        const minPx = px(win.getComputedStyle(page).minHeight);
        const reservePx = Math.max(0, win.innerHeight - minPx);
        page.style.zoom = scale.toFixed(4);
        if (minPx > 0) {
          page.style.minHeight = `calc((100vh - ${Math.round(reservePx)}px) / ${scale.toFixed(4)})`;
        }
        result = 'shrunk to one page';
      } else if (scale < 1) {
        result = repeatHeaderOnEveryPage(doc, win, page)
          ? 'multi-page, header repeated on each page'
          : 'multi-page (no .report-repeat-header found)';
      }

      console.info(
        `fitReportToPage: content ${Math.round(natural)}px, page room ${Math.round(budget)}px, ` +
        `scale ${scale.toFixed(3)} -> ${result}`
      );
    });

    simulated.remove();
  } catch (e) {
    console.error('fitReportToPage: could not fit report, printing as-is', e);
  }

  win.focus();
  win.print();
  return Promise.resolve();
};

export default fitReportToPage;
